import type React from 'react';
import { useCallback, useRef } from 'react';
import { useWordsHappenTheme } from '../../theme';
import styles from './bottom-nav.module.scss';

export type BottomNavKey = 'home' | 'daily' | 'play' | 'leagues' | 'friends';

export interface BottomNavProps {
  active: BottomNavKey;
  onNav: (key: BottomNavKey) => void;
  badges?: Partial<Record<BottomNavKey, number>>;
  disabledTabs?: BottomNavKey[];
  className?: string;
}

const TAB_ORDER: BottomNavKey[] = ['home', 'daily', 'play', 'leagues', 'friends'];

const TAB_LABELS: Record<BottomNavKey, string> = {
  home: 'Home',
  daily: 'Daily',
  play: 'Play',
  leagues: 'Leagues',
  friends: 'Friends',
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const HomeIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 12L12 3l9 9"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="2"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
    />
    <path
      d="M3 10h18M8 3v4M16 3v4"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// Placeholder "W" mark — replaced when Design delivers the final SVG asset.
const WMarkIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="14" fill="var(--wh-ink)" />
    <text
      x="14"
      y="19"
      textAnchor="middle"
      fill="var(--wh-bg)"
      fontSize="14"
      fontWeight="700"
      fontFamily="system-ui, sans-serif"
    >
      W
    </text>
  </svg>
);

const TrophyIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M8 21h8M12 17v4"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M5 3h14v8a7 7 0 01-14 0V3z" stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'} strokeWidth="2" />
    <path
      d="M5 7H2a2 2 0 002 2h1M19 7h3a2 2 0 01-2 2h-1"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
    />
  </svg>
);

const UsersIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="7" r="4" stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'} strokeWidth="2" />
    <path
      d="M2 21v-1a7 7 0 0114 0v1"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16 3.13a4 4 0 010 7.75M22 21v-1a4 4 0 00-3-3.87"
      stroke={active ? 'var(--wh-ink)' : 'var(--wh-ink-faint)'}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// ── Badge ─────────────────────────────────────────────────────────────────────

const Badge: React.FC<{ count: number }> = ({ count }) => (
  <span className={styles.badge} aria-hidden="true">
    {count >= 100 ? '99+' : count}
  </span>
);

// ── Tab ───────────────────────────────────────────────────────────────────────

interface TabProps {
  tabKey: BottomNavKey;
  active: boolean;
  disabled: boolean;
  badge?: number;
  onNav: (key: BottomNavKey) => void;
  tabRef?: React.Ref<HTMLButtonElement>;
}

const Tab: React.FC<TabProps> = ({ tabKey, active, disabled, badge, onNav, tabRef }) => {
  const label = TAB_LABELS[tabKey];
  const hasBadge = badge != null && badge > 0;
  const badgeText = hasBadge ? (badge >= 100 ? '99+' : String(badge)) : undefined;

  const ariaLabel = hasBadge ? `${label}, ${badgeText} unread` : label;

  const handleClick = () => {
    if (!disabled && !active) {
      onNav(tabKey);
    }
  };

  const isPlay = tabKey === 'play';

  return (
    <button
      ref={tabRef}
      type="button"
      role="tab"
      aria-selected={active}
      aria-disabled={disabled ? 'true' : undefined}
      aria-label={ariaLabel}
      className={[
        styles.tab,
        isPlay ? styles.tabPlay : null,
        active ? styles.tabActive : null,
        disabled ? styles.tabDisabled : null,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      tabIndex={active ? 0 : -1}
    >
      {isPlay ? (
        <span className={styles.playCircle}>
          <WMarkIcon />
        </span>
      ) : (
        <span className={styles.icon}>
          {tabKey === 'home' && <HomeIcon active={active} />}
          {tabKey === 'daily' && <CalendarIcon active={active} />}
          {tabKey === 'leagues' && <TrophyIcon active={active} />}
          {tabKey === 'friends' && <UsersIcon active={active} />}
          {hasBadge && <Badge count={badge!} />}
        </span>
      )}
      <span className={styles.tabLabel}>{label}</span>
    </button>
  );
};

// ── BottomNav ─────────────────────────────────────────────────────────────────

export const BottomNav: React.FC<BottomNavProps> = ({ active, onNav, badges = {}, disabledTabs = [], className }) => {
  const { theme } = useWordsHappenTheme();
  const isMeadow = theme === 'meadow';

  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = TAB_ORDER.indexOf(active);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % TAB_ORDER.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      } else if (e.key === 'Enter' || e.key === ' ') {
        return;
      } else {
        return;
      }

      e.preventDefault();
      const nextKey = TAB_ORDER[nextIndex];
      if (!disabledTabs.includes(nextKey)) {
        onNav(nextKey);
      }
      tabRefs.current[nextIndex]?.focus();
    },
    [active, disabledTabs, onNav],
  );

  return (
    // The centre play tab uses position:absolute with negative top to overlap this bar by ~8px.
    // Ancestor containers must NOT apply overflow:hidden or the raised tab will be clipped.
    <div
      role="tablist"
      aria-label="Main navigation"
      className={[styles.nav, isMeadow ? styles.meadow : styles.flat, className].filter(Boolean).join(' ')}
      onKeyDown={handleKeyDown}
    >
      {TAB_ORDER.map((key, index) => (
        <Tab
          key={key}
          tabKey={key}
          active={active === key}
          disabled={disabledTabs.includes(key)}
          badge={badges[key]}
          onNav={onNav}
          tabRef={(el) => {
            tabRefs.current[index] = el;
          }}
        />
      ))}
    </div>
  );
};
